import React from 'react';

interface FormField {
  id: number;
  field_name: string;
  field_type: string;
  label: string;
  description: string | null;
  required: boolean;
  placeholder: string | null;
  default_value: string | null;
  options: Array<{ label: string; value: string }> | null;
}

interface DynamicFormRendererProps {
  fields: FormField[];
  values: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
}

export default function DynamicFormRenderer({ fields, values, onChange }: DynamicFormRendererProps) {
  const renderField = (field: FormField) => {
    const value = values[field.field_name] || field.default_value || '';

    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || ''}
            className="w-full px-3 py-2 border rounded-lg"
            required={field.required}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || ''}
            rows={3}
            className="w-full px-3 py-2 border rounded-lg"
            required={field.required}
          />
        );

      case 'number':
      case 'currency':
      case 'percentage':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(field.field_name, parseFloat(e.target.value) || 0)}
            placeholder={field.placeholder || ''}
            className="w-full px-3 py-2 border rounded-lg"
            required={field.required}
            step={field.field_type === 'currency' ? '0.01' : '1'}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            required={field.required}
          />
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <label key={option.value} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={field.field_name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => onChange(field.field_name, e.target.value)}
                  required={field.required}
                  className="rounded"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <label key={option.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  value={option.value}
                  checked={(value || []).includes(option.value)}
                  onChange={(e) => {
                    const currentValues = value || [];
                    const newValues = e.target.checked
                      ? [...currentValues, option.value]
                      : currentValues.filter((v: string) => v !== option.value);
                    onChange(field.field_name, newValues);
                  }}
                  className="rounded"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            required={field.required}
          >
            <option value="">Select...</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'file':
        return (
          <input
            type="file"
            onChange={(e) => onChange(field.field_name, e.target.files?.[0])}
            className="w-full px-3 py-2 border rounded-lg"
            required={field.required}
          />
        );

      case 'header':
        return (
          <div className="col-span-2">
            <h3 className="text-lg font-semibold text-gray-900">{field.label}</h3>
            {field.description && <p className="text-sm text-gray-600">{field.description}</p>}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(field.field_name, e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            required={field.required}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      {fields.map((field) => (
        <div key={field.id} className={field.field_type === 'header' ? 'col-span-2' : ''}>
          {field.field_type !== 'header' && (
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
          )}
          {field.description && field.field_type !== 'header' && (
            <p className="text-xs text-gray-500 mb-2">{field.description}</p>
          )}
          {renderField(field)}
        </div>
      ))}
    </div>
  );
}
